//@flow
import React, {useCallback, useState} from 'react';
import {BreadcrumbItem, ConfirmDialog, PageHeader, TextInput} from "../lib/Components";
import HomeScreen from "./HomeScreen";
import msgs, {formatString} from "../lib/Language";
import {Breadcrumb, Button, Form, Spinner} from "react-bootstrap";
import {store} from "../application";

/**
 * Pomocniczy komponent akcji administracyjnej.

 */
const Action=({ children, action, warning, disabled, title, warningAcceptLabel, form }: {

    /**
     * Tytuł akcji administracyjnej.
     */
    title: string;
    /**
     * Endpoint akcji administracyjnej lub funkcja ją wykonująca.
     */
    action: string | () => void | () => Promise<void> | () => Promise<boolean> | () => Promise<string>;

    /** Etykieta akcji */
    children?: React$Node;
    /**
     * Komunikat ostrzegawczy wyświetlany przed wykonaniem akcji.
     * Oznacza to, że akcja jest potencjalnie niebezpieczna i wymaga potwierdzenia.
     */
    warning?: React$Node|true;

    /**
     * Etykieta przycisku akceptującego ostrzeżenie.
     */
    warningAcceptLabel?: string;
    
    /** Czy akcja jest wyłączona */
    disabled?: boolean;

    /**
     * Dodatkowa treść/dane/formularz.
     */
    form?: React$Node;
    
}) => {
    const [ modal, setModal ] = useState<boolean>(null);
    const [ processing, setProcessing ] = useState<boolean>(false);

    const executeAction = useCallback(async () => {
        if(processing) return;
        setProcessing(true);
        setModal(false);
        try {
            if(typeof(action)==='function') {
                const result = await action();
                if(typeof(result)==='string') {
                    window.alert(result);
                } else if(typeof(result)==='boolean') {
                    if(!result) {
                        window.alert(msgs.admin.actionAdminActionFailed);
                    } else {
                        window.alert(msgs.admin.actionAdminActionCompleted);
                    }
                } else if(result===null || result===undefined) {
                    window.alert(msgs.admin.actionAdminActionCompleted);
                }
            }
        }finally {
            setProcessing(false);
        }

    }, [ action, processing ]);

    const handleClick = useCallback(async () => {
        if(warning) {
            setModal(true);
            return;
        }
        return await executeAction();
    }, [ warning, executeAction ]);

    if(warning===true) {
        warning=formatString(msgs.admin.messageAdminActionDangerousConfirm, title);
    }

    return <div className="admin-action my-3">
        {form && <div>{form}</div>}
        <Button onClick={handleClick} disabled={disabled || processing}>
            {processing && <Spinner animation="border"/>} {title}
        </Button>
        {children && <p className="text-info mt-3">
            {children}
        </p>}
        {(modal!==null && warning) && <ConfirmDialog
            show={modal}
            title={msgs.admin.titleAdminActionDangerousConfirm}
            onHide={() => setModal(false)}
            onAccept={executeAction}
            variant="danger"
            accept={warningAcceptLabel || msgs.admin.actionExecuteAdminAction}
            >{warning}</ConfirmDialog>
        }
        <hr/>
    </div>
}

const ImportOrganizationsAction = () => {
    const [ ids, setIds ] = useState('');
    const handleImport=async () => {
        // Proszę podać listę ID organizacji z EC, które mają zostać zaimportowane, oddzielając je przecinkami, spacjami lub znakiem nowej linii. Przykład: 123,456,789
        const ecIds=ids.split(/[\s,]+/).map(id => id.trim()).filter(id => id.length>0);
        if(ecIds.length===0) {
            window.alert(msgs.admin.adminActionImportOrganizationsNoIds);
            return;
        }
        const errors=await store.userApi.importEcOrganizations(ecIds);
        setIds("");
        return errors;
    }

    return <Action
        form={<Form.Control
            as="textarea"
            rows={3}
            width={'100%'}
            value={ids}
            onChange={e => setIds(e.target.value)}
            placeholder={msgs.admin.adminActionImportOrganizationsPlaceHolder}
            className="mb-3"
        />}
        title={msgs.admin.adminActionImportOrganizations}
        action={handleImport}
        warning={false}
    />

}

/**
 * Ekran z akcjami administracyjnymi.
 * @author Ryszard Trojnacki
 */
const AdminActionsScreen = () => {
    return <>
        <Breadcrumb>
            <BreadcrumbItem to={HomeScreen.url}>{msgs.admin.menuDesktop}</BreadcrumbItem>
            <BreadcrumbItem active>{msgs.admin.menuAdminActions}</BreadcrumbItem>
        </Breadcrumb>
        <PageHeader title={msgs.admin.menuAdminActions}/>
        <div className="tab-content">
            <Action
                title={msgs.admin.adminActionTerms}
                action={store.userApi.resetTerms}
                warning={msgs.admin.adminActionTermsConfirm}
            >
                {msgs.admin.adminActionTermsDescription}
            </Action>
            <Action
                title={msgs.admin.adminActionSyncVindications}
                action={store.userApi.adminSyncVindications}
                warning={false}/>
            <Action
                title={msgs.admin.adminActionSyncDebtExchange}
                action={store.userApi.adminSyncDebtExchange}
                warning={false}/>
            <Action
                title={msgs.admin.adminActionInvoiceClientsUpdate}
                action={store.userApi.adminInvoiceClientsUpdate}
                warning={false}>{msgs.admin.adminActionInvoiceClientsUpdateDescription}</Action>
            <ImportOrganizationsAction/>
        </div>
    </>
}
AdminActionsScreen.url='/admin_actions';

export default AdminActionsScreen;